import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UserPlus, Users, WifiOff } from 'lucide-react'
import { useState } from 'react'

import { AddMemberDialog } from './AddMemberDialog'
import { ASSIGNABLE_ROLES, ROLES, roleLabel, type AssignableRoleId } from './roles'
import type { BillingUsage, TeamMember, UpdateUserBody } from '../../api/types.gen'
import { ApiException, api } from '../../api/client'
import { formatDate } from '../billing/types'
import { usePageTitle } from '../../lib/usePageTitle'
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Skeleton,
  cx,
} from '../../ui'
import { useSession } from '../auth/useSession'

type Member = TeamMember

type Usage = Pick<BillingUsage, 'users'>

/**
 * El equipo del estudio.
 *
 * Los cuatro endpoints existían desde F0 y no había pantalla, así que un
 * estudio que pagaba el plan Agencia —«10 usuarios» en la tabla de precios— no
 * tenía forma de añadir a nadie. Se cobraba algo que no se entregaba.
 */
export function TeamPage() {
  usePageTitle('Tu equipo')
  const queryClient = useQueryClient()
  const yo = useSession((state) => state.user)
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<Member | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [resent, setResent] = useState<string | null>(null)

  const members = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ data: Member[] }>('/users'),
  })

  // El plan limita cuánta gente cabe, y el backend lo hace cumplir al crear. Sin
  // enseñarlo aquí, el estudio descubre el tope chocando contra un error.
  const plan = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () =>
      api.get<{ data: { usage: Usage; plan: { max_users: number } | null } }>(
        '/billing/subscription',
      ),
  })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['users'] })
    void queryClient.invalidateQueries({ queryKey: ['billing'] })
  }

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: AssignableRoleId }) =>
      api.patch<{ data: TeamMember }, UpdateUserBody>(`/users/${id}`, { role }),
    onSuccess: () => {
      setFailure(null)
      refresh()
    },
    onError: (error) =>
      setFailure(error instanceof ApiException ? error.message : 'No se pudo cambiar el rol.'),
  })

  /*
   | Reenviar. Los correos se pierden y el enlace caduca a la semana; sin esto
   | la única salida sería quitar a la persona y volver a añadirla, que le
   | cambia el id y deja rastro raro.
   */
  const resend = useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: { invitation_sent: boolean } }>(`/users/${id}/invitation`, {}),
    onSuccess: (result, id) => {
      setFailure(
        result.data.invitation_sent ? null : 'No se pudo enviar el correo. Inténtalo en un rato.',
      )
      setResent(result.data.invitation_sent ? id : null)
    },
    onError: (error) =>
      setFailure(error instanceof ApiException ? error.message : 'No se pudo reenviar.'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ data: unknown }>(`/users/${id}`),
    onSuccess: () => {
      setFailure(null)
      setRemoving(null)
      refresh()
    },
    onError: (error) => {
      setRemoving(null)
      setFailure(error instanceof ApiException ? error.message : 'No se pudo quitar a esta persona.')
    },
  })

  const max = plan.data?.data.plan?.max_users ?? null
  const used = plan.data?.data.usage.users ?? members.data?.data.length ?? 0
  const full = max !== null && used >= max

  return (
    <section className="flex max-w-page flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-title font-semibold text-ink">Tu equipo</h1>
          {max !== null && (
            <p className="mt-1 text-body text-ink-muted" data-numeric>
              {used} de {max} {max === 1 ? 'persona' : 'personas'} en tu plan
            </p>
          )}
        </div>

        <Button disabled={full} onClick={() => setAdding(true)}>
          <UserPlus size={17} strokeWidth={2} aria-hidden />
          Añadir
        </Button>
      </header>

      {/* El tope se dice antes de chocar con él, y con la salida al lado. */}
      {full && (
        <Banner tone="warning" title="Tu plan está lleno">
          <p>
            Ya tienes {used} de {max}. Para añadir a alguien más, sube de plan o
            quita a quien ya no trabaje contigo.
          </p>
        </Banner>
      )}

      {failure !== null && (
        <Banner tone="danger" live="alert">
          {failure}
        </Banner>
      )}

      {members.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-20 w-full rounded-card" />
          <Skeleton className="h-20 w-full rounded-card" />
        </div>
      ) : members.error ? (
        <ErrorState
          icon={WifiOff}
          title="No se pudo cargar tu equipo"
          description="Casi siempre es la conexión. Vuelve a intentarlo."
          action={
            <Button variant="secondary" onClick={() => void members.refetch()}>
              Reintentar
            </Button>
          }
        />
      ) : members.data.data.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Trabajas en solitario"
          description="Añade a quien dispare contigo o edite, y podrá subir a los mismos proyectos desde su propia cuenta."
          action={
            <Button onClick={() => setAdding(true)}>
              <UserPlus size={17} strokeWidth={2} aria-hidden />
              Añadir a alguien
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {members.data.data.map((member) => {
            const soyYo = member.id === yo?.id
            const esPropietario = member.role === 'owner'

            return (
              <li key={member.id}>
                <Card className="flex flex-wrap items-center justify-between gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-label font-medium text-ink">{member.name}</span>
                      {soyYo && <Badge tone="accent">Tú</Badge>}
                      {member.status === 'disabled' && <Badge tone="danger">Desactivado</Badge>}
                      {member.status === 'invited' && <Badge tone="warning">Invitación enviada</Badge>}
                    </div>

                    <p className="mt-0.5 truncate text-body text-ink-muted">{member.email}</p>

                    <p className="mt-1 text-caption text-ink-subtle">
                      {member.last_login_at !== null
                        ? `Última vez el ${formatDate(member.last_login_at)}`
                        : 'Todavía no ha entrado'}
                      {member.status === 'invited' && (
                        <>
                          {' · '}
                          {resent === member.id ? (
                            <span className="text-positive">Invitación reenviada</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => resend.mutate(member.id)}
                              disabled={resend.isPending}
                              className="action underline underline-offset-2 transition-colors hover:text-ink disabled:opacity-50"
                            >
                              Reenviar invitación
                            </button>
                          )}
                        </>
                      )}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {/*
                      El propietario no se toca y uno mismo tampoco: el backend
                      lo rechaza, y ofrecer un control que va a fallar es peor
                      que no ofrecerlo. Se enseña el rol como texto.
                    */}
                    {esPropietario || soyYo ? (
                      <span className="text-label text-ink-muted">{roleLabel(member.role)}</span>
                    ) : (
                      <>
                        <label className="sr-only" htmlFor={`rol-${member.id}`}>
                          Rol de {member.name}
                        </label>
                        <select
                          id={`rol-${member.id}`}
                          value={member.role}
                          disabled={changeRole.isPending}
                          onChange={(event) =>
                            changeRole.mutate({
                              id: member.id,
                              // El desplegable sólo ofrece los asignables, que son
                              // los únicos que la API acepta.
                              role: event.target.value as AssignableRoleId,
                            })
                          }
                          className={cx(
                            'rounded-control border border-line-strong bg-surface px-3 py-2',
                            'text-label text-ink disabled:opacity-50',
                          )}
                        >
                          {ASSIGNABLE_ROLES.map((id) => (
                            <option key={id} value={id}>
                              {ROLES[id].label}
                            </option>
                          ))}
                        </select>

                        <Button variant="ghost" size="sm" onClick={() => setRemoving(member)}>
                          Quitar
                        </Button>
                      </>
                    )}
                  </div>
                </Card>
              </li>
            )
          })}
        </ul>
      )}

      {adding && <AddMemberDialog onClose={() => setAdding(false)} onAdded={refresh} />}

      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={`¿Quitar a ${removing?.name ?? ''}?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              busy={remove.isPending && 'Quitando…'}
              onClick={() => removing !== null && remove.mutate(removing.id)}
            >
              Quitar del equipo
            </Button>
          </>
        }
      >
        <p className="text-body text-ink-muted">
          Dejará de poder entrar al instante y se le cerrarán las sesiones que
          tenga abiertas.
        </p>
        <p className="mt-3 text-body text-ink-muted">
          Lo que subió se queda: los proyectos y los archivos son del estudio, no
          suyos. Y libera una plaza de tu plan.
        </p>
      </Dialog>
    </section>
  )
}
